/**
 * An audit's analyses, listed as the client's checklist — one entry per
 * analysis (#1–#34), whatever computes it. An analysis with several parts
 * shows its reports inside it; analyses on hold are not listed.
 *
 * The whole set downloads as one ZIP. Analyses run on the server when someone asks
 * (here, or "Create audit and run analyses"), never because this page was
 * opened, and keep running if the page is left. See
 * docs/ui-design.md §3.
 */

import { useModule } from '../modules/useModule';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Button,
	IconButton,
	LinearProgress,
	ListItemText,
	Menu,
	MenuItem,
	Tooltip,
	Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useMutation } from '@tanstack/react-query';
import { ConfirmDialog, Note } from '../../shared/ui';
import { useToast } from '../../provider/toast';
import {
	downloadClientAnalysisExcel,
	useAnalysisOverviewQuery,
	useExportPackMutation,
	useRetryFailedMutation,
	useRunAnalysesMutation,
} from '../../queries/analysis';
import { useAuth } from '../../provider/auth';
import { useAppDispatch } from '../../store';
import { watchAudit } from '../../store/runWatchSlice';
import { ErrorState, LoadingState } from '../shared/states';
import {
	DataTable,
	FilterBar,
	useFullScreen,
	type Column,
	type FilterSpec,
} from '../shared/tables';
import { useAuditContext } from '../audits/AuditWorkspace';
import { PageHeader } from '../../provider/pageTitle';
import { count, dateTime, durationLabel } from '../../utils/format';
import {
	analysisRows,
	dataTypeLabel,
	excelDownloadReason,
	type AnalysisRow,
} from './analysisRows';
import { isBlocked, listStatus, ListStatusBadge } from './ListStatusBadge';
import { AnalysisInfo } from './AnalysisInfo';
import { AnalysisPreview } from './AnalysisPreview';
import { EditAnalysesDialog } from '../audits/AnalysisPicker';
import type { AnalysisOverviewRow } from '../../schemas';

const LOCKED_REASON =
	'The report has been issued, so analyses cannot be re-run.';

type Filter =
	| 'ALL'
	| 'READY'
	| 'RUNNING'
	| 'COMPLETED'
	| 'FINDINGS'
	| 'BLOCKED'
	| 'FAILED';

function isReady(row: AnalysisOverviewRow): boolean {
	return row.availability === 'READY' && row.status === 'NOT_RUN';
}

/** What "Run analyses" picks up: every analysis the data can feed that has
 *  not run, failed, or is out of date. Completed, current results are left
 *  alone — re-running those is the separate, confirmed "Re-run all".
 *  Availability not checked yet (an audit set up before it was recorded)
 *  counts as runnable once the audit has data: the server checks it when
 *  the run is asked for, and skips anything blocked. */
function isPending(row: AnalysisOverviewRow, auditHasData: boolean): boolean {
	if (row.availability === 'BLOCKED') return false;
	if (row.availability === null && !auditHasData) return false;
	return (
		row.status === 'NOT_RUN' ||
		row.status === 'FAILED' ||
		(row.status === 'COMPLETED' && row.outdated)
	);
}

export default function AnalysesIndex() {
	const { audit, refresh, crumbs } = useAuditContext();
	const { link, module } = useModule();
	const { permissions } = useAuth();
	const canRun = Boolean(permissions?.runAnalyses);
	const toast = useToast();
	const [filter, setFilter] = useState<Filter>('ALL');
	const [search, setSearch] = useState('');
	const [confirmRerun, setConfirmRerun] = useState(false);
	const [editing, setEditing] = useState(false);
	const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
	/** The analysis whose preview drawer is open. */
	const [preview, setPreview] = useState<AnalysisRow | null>(null);

	const overview = useAnalysisOverviewQuery(audit.pkAuditId);
	const runAll = useRunAnalysesMutation(audit.pkAuditId);
	const retryFailed = useRetryFailedMutation(audit.pkAuditId);
	const exportPack = useExportPackMutation(audit.pkAuditId);
	const download = useMutation({
		mutationFn: (analysisNumber: number) =>
			downloadClientAnalysisExcel(audit.pkAuditId, analysisNumber),
		onError: (error) =>
			toast(
				'danger',
				error instanceof Error ? error.message : 'The download failed.',
			),
	});
	/** The analysis whose re-run was just asked for, until the poll shows it. */
	const [rerunning, setRerunning] = useState<number | null>(null);
	/* Full screen sits in the filter row, outside the table. */
	const fullScreen = useFullScreen('Analyses');

	const rows = overview.data?.rows ?? [];
	const entries = useMemo(
		() => analysisRows(rows, overview.data?.analyses ?? []),
		[rows, overview.data?.analyses],
	);
	/* An analysis that leaves the list (edited out) closes its preview. */
	useEffect(() => {
		if (
			preview &&
			!entries.some((entry) => entry.number === preview.number)
		)
			setPreview(null);
	}, [entries, preview]);
	const implemented = entries.filter(
		(entry): entry is AnalysisRow & { rollup: AnalysisOverviewRow } =>
			entry.rollup !== null,
	);
	/* Every report behind a listed analysis — what the run buttons act on. */
	const reportRows = implemented.flatMap((entry) =>
		entry.reports.map((report) => report.row),
	);

	/* "Started 42s ago" has to move between polls too: a poll that returns the
     same rows does not re-render this page, so tick once a second while
     anything is running. */
	const anyRunning = rows.some((row) => row.status === 'RUNNING');
	/* A run already in progress when the page opens is watched too, so its
     "completed" still reaches the user if they move on. */
	const dispatch = useAppDispatch();
	const anyMoving = rows.some(
		(row) => row.status === 'RUNNING' || row.status === 'QUEUED',
	);
	useEffect(() => {
		if (anyMoving)
			dispatch(
				watchAudit({ auditId: audit.pkAuditId, announceStart: false }),
			);
	}, [anyMoving, audit.pkAuditId, dispatch]);
	const [, setTick] = useState(0);
	useEffect(() => {
		if (!anyRunning) return;
		const timer = setInterval(() => setTick((tick) => tick + 1), 1000);
		return () => clearInterval(timer);
	}, [anyRunning]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return entries.filter((entry) => {
			const row = entry.rollup;
			if (row === null) {
				if (filter !== 'ALL') return false;
			} else {
				if (filter === 'READY' && !isReady(row)) return false;
				if (
					filter === 'RUNNING' &&
					row.status !== 'RUNNING' &&
					row.status !== 'QUEUED'
				)
					return false;
				if (filter === 'COMPLETED' && row.status !== 'COMPLETED')
					return false;
				if (
					filter === 'FINDINGS' &&
					!((row.exceptionFindings ?? 0) > 0)
				)
					return false;
				if (filter === 'BLOCKED' && !isBlocked(row)) return false;
				if (filter === 'FAILED' && row.status !== 'FAILED')
					return false;
			}
			const text = [
				`#${entry.number}`,
				entry.title,
				...entry.reports.map((r) => r.label),
			]
				.join(' ')
				.toLowerCase();
			if (q && !text.includes(q)) return false;
			return true;
		});
	}, [entries, filter, search]);

	if (overview.isPending) return <LoadingState label="Loading analyses…" />;
	if (overview.error)
		return <ErrorState error={overview.error} onRetry={overview.refetch} />;
	if (!overview.data) return null;

	const { locked } = overview.data;
	const rollups = implemented.map((entry) => entry.rollup);
	const summary = {
		total: implemented.length,
		completed: rollups.filter((row) => row.status === 'COMPLETED').length,
		withFindings: rollups.filter(
			(row) =>
				row.status === 'COMPLETED' && (row.exceptionFindings ?? 0) > 0,
		).length,
		failed: rollups.filter((row) => row.status === 'FAILED').length,
		outdated: rollups.filter((row) => row.outdated).length,
		withExcel: reportRows.filter((row) => row.hasExcel).length,
	};
	const readyCount = rollups.filter(isReady).length;
	const blockedCount = rollups.filter(isBlocked).length;
	const runningCount = rollups.filter(
		(row) => row.status === 'RUNNING' || row.status === 'QUEUED',
	).length;

	const auditHasData = audit.rowCount > 0;
	const pendingSlugs = reportRows
		.filter((row) => isPending(row, auditHasData))
		.map((row) => row.slug);
	const allSlugs = reportRows.map((row) => row.slug);
	const allBlocked = reportRows.length > 0 && reportRows.every(isBlocked);

	/* Explicit slugs run just those; none means "every chosen analysis". */
	const start = (slugs?: string[]) =>
		runAll.mutate(slugs, {
			onSuccess: () => {
				void refresh();
				toast(
					'ok',
					slugs?.length === 1
						? 'Analysis started. You can leave this page.'
						: 'Analyses started. You can leave this page.',
				);
			},
			onError: (error) =>
				toast(
					'danger',
					error instanceof Error ? error.message : 'Could not start.',
				),
		});

	const runRetryFailed = () =>
		retryFailed.mutate(undefined, {
			onSuccess: () => toast('ok', 'Retrying the failed analyses.'),
			onError: (error) =>
				toast(
					'danger',
					error instanceof Error ? error.message : 'Could not retry.',
				),
		});

	/* "Run analyses" is always the primary action (spec §19): it runs what is
     still to do — not run, failed or out of date — and never touches a
     current result. "Retry failed" and "Re-run all" sit beside it as
     secondary actions, each only when it applies. */
	const runDisabledReason = locked
		? LOCKED_REASON
		: reportRows.length === 0
			? 'No analyses are selected for this audit.'
			: allBlocked
				? 'Every analysis is blocked by the selected data.'
				: pendingSlugs.length > 0
					? undefined
					: runningCount > 0
						? 'The remaining analyses are already running.'
						: 'Every analysis is up to date.';
	/* Only when a failed analysis could actually run again: one the data
     scope now blocks would just be refused. */
	const showRetryFailed = reportRows.some(
		(row) => row.status === 'FAILED' && !isBlocked(row),
	);
	const showRerunAll =
		pendingSlugs.length === 0 && reportRows.length > 0 && !allBlocked;
	const rerunDisabledReason = locked
		? LOCKED_REASON
		: runningCount > 0
			? 'Wait for the running analyses to finish.'
			: undefined;
	const editDisabledReason = locked
		? 'The report has been issued, so the analyses can no longer change.'
		: runningCount > 0
			? 'Wait for the running analyses to finish.'
			: undefined;
	const busy = runAll.isPending || retryFailed.isPending;

	const summaryLine = [
		`${count(summary.completed)} of ${count(summary.total)} analyses complete`,
		summary.withFindings > 0
			? `${count(summary.withFindings)} with findings`
			: null,
		summary.failed > 0 ? `${count(summary.failed)} failed` : null,
	]
		.filter(Boolean)
		.join(' · ');

	const specs: FilterSpec[] = [
		{
			key: 'status',
			label: 'Show',
			kind: 'chips',
			options: [
				{ value: '', label: `All ${count(entries.length)}` },
				{ value: 'READY', label: `Ready ${count(readyCount)}` },
				{ value: 'RUNNING', label: `Running ${count(runningCount)}` },
				{
					value: 'COMPLETED',
					label: `Completed ${count(summary.completed)}`,
				},
				{
					value: 'FINDINGS',
					label: `Findings ${count(summary.withFindings)}`,
				},
				{ value: 'BLOCKED', label: `Blocked ${count(blockedCount)}` },
				{ value: 'FAILED', label: `Failed ${count(summary.failed)}` },
			],
		},
		{
			key: 'q',
			label: 'Search',
			kind: 'search',
			placeholder: 'Analysis no. or name',
		},
	];

	const analysisPath = (entry: AnalysisRow) =>
		link(`/audits/${audit.pkAuditId}/analyses/${entry.number}`);

	/* Running serial number over the analyses shown (1, 2, 3 …), fixed to the
     analysis so it does not change when the table is sorted or filtered. */
	const serialOf = new Map(
		entries.map((entry, index) => [entry.number, index + 1]),
	);

	const columns: Array<Column<AnalysisRow>> = [
		{
			key: 'serial',
			label: 'Sr no.',
			width: 50,
			sortValue: (entry) => serialOf.get(entry.number) ?? 0,
			render: (entry) => serialOf.get(entry.number),
		},
		{
			key: 'name',
			label: 'Analysis',
			width: 250,
			sortValue: (entry) => entry.number,
			render: (entry) => (
				<Box className="stack2">
					<Typography component="span" variant="inherit">
						<Link
							to={analysisPath(entry)}
							className="link b"
							onClick={(event) => event.stopPropagation()}
						>
							{entry.number}. {entry.title}
						</Link>
						<AnalysisInfo description={entry.description} />
					</Typography>
					{entry.rollup?.status === 'FAILED' &&
						entry.rollup.error && (
							<Box className="l2 text-danger">
								{entry.rollup.error}
							</Box>
						)}
				</Box>
			),
		},
		{
			key: 'data',
			label: 'Based on',
			width: 110,
			sortValue: (entry) =>
				entry.rollup
					? dataTypeLabel(entry.rollup.dataType, module)
					: '',
			render: (entry) =>
				entry.rollup
					? dataTypeLabel(entry.rollup.dataType, module)
					: '—',
		},
		{
			key: 'status',
			label: 'Status',
			width: 110,
			sortValue: (entry) =>
				entry.rollup ? listStatus(entry.rollup) : '',
			render: (entry) =>
				entry.rollup ? (
					<ListStatusBadge
						row={entry.rollup}
						running={rerunning === entry.number && runAll.isPending}
					/>
				) : null,
		},
		{
			key: 'duration',
			label: 'Time taken',
			width: 100,
			sortValue: (entry) => entry.rollup?.durationMs ?? -1,
			render: (entry) =>
				entry.rollup?.status === 'COMPLETED' &&
				entry.rollup.durationMs !== null
					? durationLabel(entry.rollup.durationMs)
					: '—',
		},
		/* Two counts, side by side: every row the analysis returned, and how many
       of those it flagged as exceptions. For a check (every returned row is a
       problem) the two are equal; for a summary they differ. */
		{
			key: 'resultRows',
			label: 'Result rows',
			num: true,
			width: 100,
			sortValue: (entry) =>
				entry.rollup?.status === 'COMPLETED'
					? (entry.rollup.resultRows ?? 0)
					: -1,
			render: (entry) =>
				entry.rollup?.status === 'COMPLETED' &&
				entry.rollup.resultRows !== null
					? count(entry.rollup.resultRows)
					: '—',
		},
		{
			key: 'findings',
			label: 'Exception findings',
			num: true,
			width: 120,
			sortValue: (entry) =>
				entry.rollup?.status === 'COMPLETED'
					? (entry.rollup.exceptionFindings ?? 0)
					: -1,
			render: (entry) =>
				entry.rollup?.status === 'COMPLETED' &&
				entry.rollup.exceptionFindings !== null
					? count(entry.rollup.exceptionFindings)
					: '—',
		},
		{
			key: 'lastRun',
			label: 'Last run',
			width: 160,
			sortValue: (entry) => entry.rollup?.completedAt ?? '',
			render: (entry) => (
				<Typography
					component="span"
					variant="inherit"
					className="nowrap"
				>
					{entry.rollup?.completedAt
						? dateTime(entry.rollup.completedAt)
						: '—'}
				</Typography>
			),
		},
		{
			key: 'actions',
			label: 'Actions',
			width: 240,
			render: (entry) => {
				const row = entry.rollup;
				if (!row) return null;
				const status = listStatus(row);
				const busyRow =
					status === 'RUNNING' ||
					(rerunning === entry.number && runAll.isPending);
				const slugs = entry.reports.map((report) => report.row.slug);
				const downloadReason = excelDownloadReason(
					{ ...row, listStatus: status },
					download.isPending && download.variables !== entry.number,
				);
				return (
					<Box
						display="flex"
						gap="var(--s1)"
						alignItems="center"
						flexWrap="nowrap"
						onClick={(event) => event.stopPropagation()}
					>
						<Tooltip
							title={downloadReason ?? ''}
							disableHoverListener={!downloadReason}
						>
							<span>
								<Button
									size="small"
									variant="contained"
									startIcon={<FileDownloadOutlinedIcon />}
									loading={
										download.isPending &&
										download.variables === entry.number
									}
									disabled={
										Boolean(downloadReason) ||
										download.isPending
									}
									aria-label={`Download Excel for analysis ${entry.number}`}
									onClick={() =>
										download.mutate(entry.number)
									}
								>
									Download Excel
								</Button>
							</span>
						</Tooltip>
						{canRun && !isBlocked(row) && (
							<Button
								size="small"
								variant="outlined"
								disabled={locked || busyRow || busy}
								title={locked ? LOCKED_REASON : undefined}
								onClick={() => {
									setRerunning(entry.number);
									start(slugs);
								}}
							>
								{status === 'FAILED'
									? 'Retry'
									: status === 'NOT_RUN'
										? 'Run'
										: 'Re-run'}
							</Button>
						)}
					</Box>
				);
			},
		},
	];

	/* While anything runs: how far the audit has got, as completed analyses
     out of all shown. */
	const doneCount = rollups.filter(
		(row) => row.status === 'COMPLETED',
	).length;
	const progress =
		summary.total > 0 ? Math.round((doneCount / summary.total) * 100) : 0;

	return (
		<Box className="stack">
			<PageHeader
				breadcrumbs={crumbs}
				title="Analyses"
				subtitle={summaryLine}
				actions={
					<Box
						display="flex"
						gap="var(--s2)"
						flexWrap="wrap"
						alignItems="center"
					>
						<Button
							variant="outlined"
							loading={exportPack.isPending}
							disabled={
								summary.withExcel === 0 || exportPack.isPending
							}
							title={
								summary.withExcel === 0
									? 'No results to download yet.'
									: undefined
							}
							onClick={() =>
								exportPack.mutate(undefined, {
									onError: (error) =>
										toast(
											'danger',
											error instanceof Error
												? error.message
												: 'The ZIP could not be prepared.',
										),
								})
							}
						>
							Download all results (ZIP)
						</Button>
						{canRun && showRetryFailed && (
							<Button
								variant="outlined"
								loading={retryFailed.isPending}
								disabled={locked || busy}
								title={locked ? LOCKED_REASON : undefined}
								onClick={runRetryFailed}
							>
								Retry failed
							</Button>
						)}
						<Tooltip title="More actions">
							<IconButton
								aria-label="More analysis actions"
								aria-haspopup="menu"
								onClick={(event) =>
									setMoreAnchor(event.currentTarget)
								}
							>
								<MoreHorizIcon />
							</IconButton>
						</Tooltip>
						{canRun && (
							<Button
								variant="contained"
								loading={runAll.isPending && rerunning === null}
								disabled={Boolean(runDisabledReason) || busy}
								title={runDisabledReason}
								onClick={() => {
									setRerunning(null);
									start(pendingSlugs);
								}}
							>
								Run analyses
							</Button>
						)}
					</Box>
				}
			/>

			<Menu
				anchorEl={moreAnchor}
				open={Boolean(moreAnchor)}
				onClose={() => setMoreAnchor(null)}
			>
				<MenuItem
					disabled={Boolean(editDisabledReason)}
					onClick={() => {
						setMoreAnchor(null);
						setEditing(true);
					}}
				>
					<ListItemText
						primary="Edit analyses"
						secondary={editDisabledReason}
					/>
				</MenuItem>
				{canRun && showRerunAll && (
					<MenuItem
						disabled={Boolean(rerunDisabledReason) || busy}
						onClick={() => {
							setMoreAnchor(null);
							setConfirmRerun(true);
						}}
					>
						<ListItemText
							primary="Re-run all…"
							secondary={rerunDisabledReason}
						/>
					</MenuItem>
				)}
			</Menu>

			{locked && (
				<Note tone="info" row>
					{LOCKED_REASON}
				</Note>
			)}

			{summary.outdated > 0 && !locked && (
				<Note
					tone="warn"
					row
					title={`${count(summary.outdated)} results are out of date`}
				>
					The data scope changed after they ran. Re-run the analyses
					to update them.
				</Note>
			)}

			{runningCount > 0 && (
				<Box className="stack2">
					<Box className="mut">
						Running {count(runningCount)}{' '}
						{runningCount === 1 ? 'analysis' : 'analyses'} ·{' '}
						{count(doneCount)} of {count(summary.total)} complete.
						You can leave this page; they keep running.
					</Box>
					<LinearProgress
						variant="determinate"
						value={progress}
						aria-label="Analyses complete"
					/>
				</Box>
			)}

			<FilterBar
				specs={specs}
				defaultOpen
				values={{ status: filter === 'ALL' ? '' : filter, q: search }}
				onChange={(key, value) => {
					if (key === 'status') setFilter((value || 'ALL') as Filter);
					else setSearch(value);
				}}
				onClear={() => {
					setFilter('ALL');
					setSearch('');
				}}
				resultNote={`${count(filtered.length)} of ${count(entries.length)}`}
				actions={fullScreen.button}
			/>

			<Box className="card">
				<Box className="card-bd flush">
					<DataTable
						rows={filtered}
						columns={columns}
						fullScreen={fullScreen}
						rowKey={(entry) => String(entry.number)}
						onRowClick={(entry) => setPreview(entry)}
						caption="Analyses"
						empty="No analysis matches these filters."
					/>
				</Box>
			</Box>

			<AnalysisPreview
				entry={
					preview
						? (entries.find((e) => e.number === preview.number) ??
							null)
						: null
				}
				auditId={audit.pkAuditId}
				analysisPath={analysisPath}
				onClose={() => setPreview(null)}
				downloadExcel={(n) => download.mutate(n)}
				downloading={
					download.isPending ? (download.variables ?? null) : null
				}
			/>

			<EditAnalysesDialog
				open={editing}
				auditId={audit.pkAuditId}
				onClose={() => setEditing(false)}
			/>

			<ConfirmDialog
				open={confirmRerun}
				onClose={() => setConfirmRerun(false)}
				onConfirm={() => {
					setConfirmRerun(false);
					start(allSlugs);
				}}
				title={`Re-run all ${count(summary.total)} analyses?`}
				danger={false}
				confirmLabel="Re-run all"
				question={
					<>
						Current results will be replaced by new ones computed
						from the same data scope.
						{audit.latestReportVersion !== null && (
							<>
								{' '}
								Report version {audit.latestReportVersion} was
								generated from the current results; after
								re-running, the results will differ from that
								version until a new one is generated.
							</>
						)}
					</>
				}
			/>
		</Box>
	);
}
